import React, { useEffect, useState } from 'react'
import CustomAlert from '../../../../components/shared/CustomAlert'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'
import { MDBContainer, MDBRow, MDBCol } from 'mdb-react-ui-kit'
import Gauge from './../../../../components/gauge/Gauge'
import KpiDashboardStore from './../../store/index'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import UserPerformanceEntryService from './../../../../shared/services/UserPerformanceEntry.service'
import LedgerFactory from './../../../../shared/factories/ledger.factory'
import LedgerTransactionFactory from './../../../../shared/factories/ledger-transaction.factory'

import './KpiDashboard.scss'

const getThisMonday = () => moment().startOf('isoWeek').format('YYYY-MM-DD')

const KpiDashboard = props => {
  const data = props?.data

  const { getKpis } = KpiDashboardStore
  const [kpis, setKpis] = useState({})
  const [marketing, setMarketing] = useState({
    invested: 0,
    leadSpend: 0,
    loaded: false,
  })

  const getLabelByKey = key => `${kpis && kpis.hasOwnProperty(key) ? kpis[key]?.label : ''}`

  const getDescriptionByKey = key => `${kpis && kpis.hasOwnProperty(key) ? kpis[key]?.description : ''}`

  const inverse = val => Math.abs(1 - parseFloat(val))

  useEffect(() => {
    getKpis().then(setKpis)
  }, [getKpis])

  useEffect(() => {
    const load = async () => {
      const thisMonday = getThisMonday()
      const thisSunday = moment().endOf('isoWeek').format('YYYY-MM-DD')
      const userId = UserProfileService.getUserId()
      if (!userId) {
        setMarketing(m => ({ ...m, loaded: true }))
        return
      }
      try {
        const [entryRes, ledgers] = await Promise.all([
          UserPerformanceEntryService.search({
            search: { user_id: userId, entry_date: thisMonday },
          }),
          LedgerFactory.search({
            search: { user_id: userId },
            pagination: false,
          }),
        ])
        const entry = (entryRes?.models || [])[0]
        const invested = entry ? Number(entry.invested) || 0 : 0

        const ledgerList = Array.isArray(ledgers) ? ledgers : ledgers?.models || []
        const ledger = ledgerList[0]
        let leadSpend = 0
        if (ledger) {
          const ledgerId = ledger.get ? ledger.get('id') : ledger.id?.() ?? ledger?.id
          const txRes = await LedgerTransactionFactory.search({
            search: { ledger_id: ledgerId, type: 'LeadVendorTransfer' },
            pagination: false,
          })
          const txList = Array.isArray(txRes) ? txRes : txRes?.models || []
          const weekStartM = moment(thisMonday)
          const weekEndM = moment(thisSunday).endOf('day')
          const weekTx = txList.filter(t => {
            const at = moment(t.get('transact_at'))
            return at.isSameOrAfter(weekStartM, 'day') && at.isSameOrBefore(weekEndM, 'day')
          })
          leadSpend = weekTx.reduce((sum, t) => sum + (parseFloat(t.get('debit')) || 0), 0)
        }
        setMarketing({ invested, leadSpend, loaded: true })
      } catch (err) {
        setMarketing(m => ({ ...m, loaded: true }))
      }
    }
    load()
  }, [])

  const renderWarning = isGoal => (
    <>
      {isGoal ? (
        <CustomAlert className='incomplete-goals' color='danger'>
          Complete your goals to start forecasting.
        </CustomAlert>
      ) : (
        <small>Unable to forecast</small>
      )}
    </>
  )

  const renderGauge = key => {
    return (
      <div className='kpi-chart'>
        <Gauge
          name={key}
          value={inverse(parseFloat(data[key].forecast.outcome) > 1 ? 1 : parseFloat(data[key].forecast.outcome))}
        />
      </div>
    )
  }

  const renderMarketingCard = () => (
    <MDBCol key='marketing' size='12' sm='6' md='4' lg='4'>
      <MDBContainer fluid className='kpi-widget-container'>
        <MDBRow>
          <MDBCol size='12' className='label'>
            <h4>Marketing</h4>
          </MDBCol>
          <MDBCol size='12' className='description'>
            <p>Weekly marketing commitment and lead spend from ledger.</p>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol size='6' className='avg-pw'>
            <table width='100%'>
              <tbody>
                <tr>
                  <td>
                    <div>AVG</div>
                    <small>Per Week</small>
                  </td>
                  <td>
                    <span>{marketing.loaded ? marketing.leadSpend.toFixed(2) : '—'}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </MDBCol>
          <MDBCol size='6' className='goal-pw'>
            <table width='100%'>
              <tbody>
                <tr>
                  <td>
                    <div>GOAL</div>
                    <small>Per Week</small>
                  </td>
                  <td>
                    <span>{marketing.loaded ? marketing.invested.toFixed(2) : '—'}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol size='12' className='tracking'>
            {renderWarning(true)}
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </MDBCol>
  )

  const renderGauges = () => {
    const cards = []

    if (data && typeof data === 'object') {
      for (let key in data) {
        if (key !== 'overview') {
          cards.push(
            <MDBCol key={'key_' + key} size='12' sm='6' md='4' lg='4'>
              <MDBContainer fluid className='kpi-widget-container'>
                <MDBRow>
                  <MDBCol size='12' className='label'>
                    <h4>{getLabelByKey(key)}</h4>
                  </MDBCol>
                  <MDBCol size='12' className='description'>
                    <p>{getDescriptionByKey(key)}</p>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='6' className='avg-pw'>
                    <table width='100%'>
                      <tbody>
                        <tr>
                          <td>
                            <div>AVG</div>
                            <small>Per Week</small>
                          </td>
                          <td>
                            <span>{Math.round(data[key]?.completed?.weekly)}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </MDBCol>
                  <MDBCol size='6' className='goal-pw'>
                    <table width='100%'>
                      <tbody>
                        <tr>
                          <td>
                            <div>GOAL</div>
                            <small>Per Week</small>
                          </td>
                          <td>
                            <span>{Math.round(data[key]?.goal?.weekly)}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </MDBCol>
                </MDBRow>
                <MDBRow>
                  <MDBCol size='12' className='tracking'>
                    {data[key]?.forecast?.outcome ? (
                      <>
                        <h6>Are you on track?</h6>
                        <span
                          style={{
                            position: 'absolute',
                            top: '3px',
                            right: '10px',
                          }}
                        >
                          {parseFloat(data[key].forecast.outcome) > 1 ? '>' : ''}
                          {(
                            (parseFloat(data[key].forecast.outcome) > 1 ? 1 : parseFloat(data[key].forecast.outcome)) *
                            100
                          ).toFixed(2)}
                          %
                        </span>
                        {renderGauge(key)}
                      </>
                    ) : (
                      <>{renderWarning(!data[key]?.goals?.weekly)}</>
                    )}
                  </MDBCol>
                </MDBRow>
              </MDBContainer>
            </MDBCol>
          )
        }
      }
    }

    cards.push(renderMarketingCard())
    return cards
  }

  return (
    <MDBContainer id='KpiDashboardComponent' fluid>
      <MDBRow>{renderGauges()}</MDBRow>
    </MDBContainer>
  )
}

export default observer(KpiDashboard)
